import ctl from '@netlify/classnames-template-literals'
import { ValidationError } from '@tanstack/react-form'
import { ReactNode } from '@tanstack/react-router'
import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react'
import { Select } from 'radix-ui'
import { useId } from 'react'
import { GenericField, GenericFieldSizing } from './generic'
import { MagicSwirl } from './magic-swirl'

export interface RadixSelectFieldProps<T> {
	options: Array<T>
	renderOption?: (option: T) => ReactNode
	value: T
	onChange: (value: T) => void
	label?: ReactNode
	magicMode?: boolean
	hint?: ReactNode
	errors?: Array<ValidationError>
	disabled?: boolean
	isWaiting?: boolean
	sizing?: GenericFieldSizing
	placeholder?: ReactNode
	hideLabel?: boolean
}

export function RadixSelectField<T extends string | number>(
	{
		options,
		value,
		onChange,
		renderOption = (option) => option,
		label,
		magicMode = false,
		hint,
		errors = [],
		disabled = false,
		isWaiting = false,
		sizing = 'medium',
		placeholder,
		hideLabel = false,
	}: RadixSelectFieldProps<T>,
) {
	const id = useId()

	const triggerClassName = ctl(`
		pr-10
		block
		relative
		group
		rounded-md ring-1 ring-grey-100 p-2
		border-none border-transparent
		data-open:border-transparent
		data-open:outline-none
		data-[state=open]:ring-blue-500 data-[state=open]:ring-1
		text-grey-800
		text-left
		w-full
		transition-all
		${magicMode ? 'text-transparent cursor-wait bg-purple-50/50' : 'text-grey-800 bg-white'}
		${disabled ? 'opacity-50 cursor-not-allowed' : ''}
		${isWaiting ? 'opacity-50 cursor-wait' : ''}
		cursor-pointer
	`)

	// Convert value to string for Radix UI Select
	const stringValue = String(value)
	const handleChange = (val: string) => {
		// Find the original option that matches the string value
		const option = options.find((opt) => String(opt) === val)
		if (option !== undefined) {
			onChange(option)
		}
	}

	return (
		<GenericField label={label} errors={errors} hint={hint} sizing={sizing} hideLabel={hideLabel}>
			<div className="has-data-[state=open]:ring-4 ring-blue-100 rounded-md w-full whitespace-normal relative">
				<Select.Root
					value={stringValue}
					onValueChange={handleChange}
					disabled={magicMode || disabled || isWaiting}
				>
					<Select.Trigger className={triggerClassName} aria-label={label ? String(label) : undefined}>
						<Select.Value placeholder={placeholder ? String(placeholder) : <>&nbsp;</>}>
							{value ? renderOption(value) : (placeholder ?? <>&nbsp;</>)}
						</Select.Value>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							fill="none"
							viewBox="0 0 10 10"
							height={10}
							width={10}
							className="absolute top-1/2 right-4 -translate-y-1/2 group-data-[state=open]:rotate-180 transition-transform duration-300 pointer-events-none"
						>
							<path
								fill="currentColor"
								d="M5 7.874a1.238 1.238 0 0 1 -0.879 -0.364L0.206 3.393A0.75 0.75 0 0 1 1.294 2.36l3.525 3.712a0.249 0.249 0 0 0 0.362 0L8.706 2.36a0.75 0.75 0 1 1 1.088 1.033L5.9 7.5a1.27 1.27 0 0 1 -0.9 0.374Z"
								strokeWidth={1}
							/>
						</svg>
					</Select.Trigger>
					<Select.Portal>
						<Select.Content
							className={ctl(`
								z-50 overflow-hidden
								max-h-[var(--radix-select-content-available-height)]
								ring-1 py-1 bg-white ring-grey-100 shadow-md rounded
								data-[state=open]:animate-in data-[state=closed]:animate-out
								data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0
								data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95
								data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2
							`)}
							position="popper"
							sideOffset={8}
							style={{ width: 'var(--radix-select-trigger-width)' }}
						>
							<Select.ScrollUpButton className="flex h-6 cursor-default items-center justify-center bg-white text-grey-600">
								<ChevronUpIcon className="size-4" />
							</Select.ScrollUpButton>
							<Select.Viewport className="px-1">
								{options.map((option) => (
									<Select.Item
										key={String(option)}
										value={String(option)}
										className={ctl(`
											text-sm rounded mx-0 pl-2 pr-10 py-2 flex gap-2 bg-white
											data-[highlighted]:bg-blue-100 data-[highlighted]:text-blue-700
											cursor-pointer whitespace-nowrap
											relative select-none outline-none
											data-[disabled]:pointer-events-none data-[disabled]:opacity-50
										`)}
									>
										<Select.ItemText>{renderOption(option)}</Select.ItemText>
									</Select.Item>
								))}
							</Select.Viewport>
							<Select.ScrollDownButton className="flex h-6 cursor-default items-center justify-center bg-white text-grey-600">
								<ChevronDownIcon className="size-4" />
							</Select.ScrollDownButton>
						</Select.Content>
					</Select.Portal>
				</Select.Root>
				{magicMode ? <MagicSwirl /> : null}
			</div>
		</GenericField>
	)
}
